import React from 'react';
import { Users } from 'lucide-react';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { cn, FOCUS_RING } from '@cotera/client/v0/lib/utils';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  GranteeAvatarStack,
  useGrantees,
} from '../../resources/list/grantee-avatar-stack';
import type { ChatHeaderMeta } from './use-chat-header-meta';

/**
 * Who else can see this chat, shown beside the Share button.
 *
 * The header had a Share button and nothing that said whether the chat was
 * already shared — so "am I talking to myself here?" was a question you could
 * only answer by opening the share dialog. Renders nothing for a private chat,
 * where the Share button already carries the whole story; this is meant to be
 * silent until there is something to say.
 *
 * Clicking it opens the same share flow as the button, so the thing that tells
 * you about sharing is also the thing that changes it.
 */
export const ChatShareIndicator: React.FC<{
  meta: ChatHeaderMeta;
  onOpenShare: () => void;
  /**
   * Drop the wording and keep the glyphs. For the copy mirrored into the
   * artifact panel's bar, which is the narrowest bar in the app and already
   * sheds breadcrumbs under pressure — "Everyone" costs more of it than the
   * fact is worth when the tooltip says the same thing in full.
   */
  compact?: boolean;
}> = ({ meta, onOpenShare, compact = false }) => {
  const orgName = useWhoami((s) => s.org.name);
  const grantees = useGrantees(meta.users, meta.ownerId);

  if (!meta.sharedWithOrg && grantees.length === 0) {
    return null;
  }

  const names = grantees
    .map((grantee) => grantee.label ?? 'Unknown member')
    .join(', ');
  const summary = meta.sharedWithOrg
    ? `Everyone at ${orgName} can see this chat`
    : `Shared with ${names}`;

  return (
    <Tooltip side="bottom" tooltipContent={summary}>
      <button
        type="button"
        onClick={onOpenShare}
        aria-label={summary}
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:bg-muted',
          FOCUS_RING
        )}
      >
        {meta.sharedWithOrg ? (
          <>
            <Users className="size-4 text-muted-foreground" />
            {compact ? null : (
              <span className="text-sm text-muted-foreground">Everyone</span>
            )}
          </>
        ) : null}
        {grantees.length > 0 ? (
          // Ringed in the header's own background: this bar is busy, and without
          // it the overlap between two circles reads as one smudged shape.
          <GranteeAvatarStack
            people={grantees}
            avatarClassName="ring-1 ring-background"
            overflowClassName="text-sm text-muted-foreground"
          />
        ) : null}
      </button>
    </Tooltip>
  );
};
